<script setup lang="ts">
import { Skeleton } from '~ds/components/ui/skeleton';
import { Heading } from '~ds/components/ui/typography';

import { RTE_RENDERER_VARIANT } from '../Input/RichTextEditor/Renderer/types';

interface FaqItem {
  heading: string;
  content: string[] | string;
}

interface Props {
  title?: string;
  headingAlign?: 'left' | 'center' | 'right';
  faqs: FaqItem[];
  maxFaqs?: number;
  isRichText?: boolean;
  backgroundColor?: 'off-white' | 'default';
  isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  headingAlign: 'left',
  maxFaqs: undefined,
  isRichText: false,
  backgroundColor: 'default',
  isLoading: false,
});

const emit = defineEmits<{
  faqOpened: [value: string];
}>();

const router = useRouter();

const displayedFaqs = computed(() => {
  return props.maxFaqs ? props.faqs.slice(0, props.maxFaqs) : props.faqs;
});

const headingClasses = computed(() => {
  return twMerge(
    cva('text-left', {
      variants: {
        headingAlign: {
          left: 'text-left',
          center: 'text-center',
          right: 'text-right',
        },
      },
    })({ headingAlign: props.headingAlign }),
  );
});

const accordionCardClasses = computed(() => {
  return twMerge(
    cva('text-left', {
      variants: {
        backgroundColor: {
          'off-white': 'bg-background',
          'default': '',
        },
      },
    })({ backgroundColor: props.backgroundColor }),
  );
});

const handleAccordionUpdate = (isOpen: boolean, faqHeading: string) => {
  if (isOpen) {
    emit('faqOpened', faqHeading);
  }
};

// "convert" all internal links into "router/nuxt" links (to prevent page reloads)
const updateLinksInContent = (event: MouseEvent) => {
  const link = (event?.target as HTMLElement)?.closest('a');
  if (link && link.hostname === window.location.hostname) {
    event.preventDefault();
    router.push(`${link.pathname}${link.search}${link.hash}`);
  }
}
</script>

<template>
  <div>
    <div :class="headingClasses">
      <slot name="title">
        <Heading
          size="2xl"
          as="h2"
          class="mb-8"
        >
          {{ title }}
        </Heading>
      </slot>
    </div>
    <div v-if="isLoading" class="flex flex-col gap-4 mb-4">
      <Skeleton class="h-20 rounded-4xl" />
      <Skeleton class="h-20 rounded-4xl" />
      <Skeleton class="h-20 rounded-4xl" />
    </div>
    <div class="flex flex-col gap-4">
      <KUICoreAccordion
        v-for="(faq, index) in displayedFaqs"
        :key="index"
        :heading="faq.heading"
        :omit-background="backgroundColor !== 'default'"
        :class="accordionCardClasses"
        @update:model-value="handleAccordionUpdate($event, faq.heading)"
      >
        <template #content>
          <template v-if="isRichText">
            <KUICoreInputRichTextEditorRenderer
              :content="faq.content as string"
              :variant="RTE_RENDERER_VARIANT.PARAGRAPH"
            />
          </template>
          <template v-else>
            <KUICoreHtml
              v-for="(text, i) in (Array.isArray(faq.content) ? faq.content : [faq.content])"
              :key="i"
              :html="text"
              class="py-2 text-lg font-normal"
              @click="updateLinksInContent"
            />
          </template>
        </template>
      </KUICoreAccordion>
    </div>
  </div>
</template>
